/**
 * Golden Horse — quote request form enhancements.
 *
 * Shopify Liquid cannot read query parameters, so the product context that the
 * "Request a Quote" button passes along (?product=<handle>&variant=<id>) has to
 * be resolved in the browser. This script fetches the product from Shopify's
 * own product JSON endpoint and fills in the product, variant and SKU fields.
 *
 * The form is fully usable without this file: the fields are ordinary text
 * inputs a buyer can complete by hand, and validation falls back to native
 * browser constraints. Nothing here fakes a submission state — the success
 * banner is rendered by Liquid only when Shopify reports posted_successfully.
 */

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @returns {HTMLInputElement|null}
 */
function field(form, name) {
  const el = form.querySelector(`[data-gh-prefill="${name}"]`);
  return el instanceof HTMLInputElement ? el : null;
}

/**
 * Resolve ?product= / ?variant= into the product and variant fields.
 * @param {HTMLFormElement} form
 */
async function applyPrefill(form) {
  const params = new URLSearchParams(window.location.search);
  const handle = params.get('product');
  const variantId = params.get('variant');

  const source = field(form, 'source');
  if (source) source.value = window.location.href;

  if (!handle) return;

  const productField = field(form, 'product');
  const variantField = field(form, 'variant');
  // Never overwrite something the visitor has already typed.
  if (!productField || productField.value.trim() !== '') return;

  let product;
  try {
    // Root-relative so locale and market prefixes are preserved.
    const root = form.closest('.gh-quote-form')?.dataset.rootUrl || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const res = await fetch(`${base}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;
    product = await res.json();
  } catch (error) {
    // A failed lookup just leaves the fields blank for manual entry.
    console.warn('[gh-quote-form] Could not resolve product context:', error);
    return;
  }

  if (!product || !product.title) return;
  productField.value = product.title;

  if (!variantField || variantField.value.trim() !== '') return;

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variant =
    variants.find((v) => String(v.id) === String(variantId)) ?? (variants.length === 1 ? variants[0] : null);
  if (!variant) return;

  const title = variant.title && variant.title !== 'Default Title' ? variant.title : '';
  if (title && variant.sku) {
    variantField.value = `${title} (${variant.sku})`;
  } else {
    variantField.value = variant.sku || title;
  }

  showSelected(form, product, variant);
}

/**
 * Show the resolved product as a small card (thumbnail, name, SKU) so the
 * buyer can see what the quote is for.
 * @param {HTMLFormElement} form
 * @param {{ title: string, featured_image?: string }} product
 * @param {{ sku?: string, featured_image?: { src?: string } }} variant
 */
function showSelected(form, product, variant) {
  const card = form.querySelector('[data-gh-selected]');
  if (!(card instanceof HTMLElement)) return;

  const titleEl = card.querySelector('[data-gh-selected-title]');
  const skuEl = card.querySelector('[data-gh-selected-sku]');
  const image = card.querySelector('[data-gh-selected-image]');

  if (titleEl) titleEl.textContent = product.title;
  if (skuEl) skuEl.textContent = variant.sku || '';

  const src = variant.featured_image?.src || product.featured_image;
  if (image instanceof HTMLImageElement && src) {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', '144');
    image.src = url.toString();
  } else if (image instanceof HTMLImageElement) {
    image.remove();
  }

  card.hidden = false;
}

/**
 * Move focus to the first invalid field rather than leaving the visitor to
 * hunt for it.
 * @param {SubmitEvent} event
 */
function handleInvalidSubmit(event) {
  const form = event.currentTarget;
  if (!(form instanceof HTMLFormElement) || form.checkValidity()) return;

  event.preventDefault();
  const firstInvalid = form.querySelector(':invalid');
  if (firstInvalid instanceof HTMLElement) {
    firstInvalid.focus();
    firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

function init() {
  for (const form of document.querySelectorAll('.gh-quote-form__form')) {
    if (!(form instanceof HTMLFormElement) || form.dataset.ghReady === 'true') continue;
    form.dataset.ghReady = 'true';
    applyPrefill(form);
    form.addEventListener('submit', handleInvalidSubmit);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Re-run after Horizon re-renders a section in the theme editor.
document.addEventListener('shopify:section:load', init);
